import React, { useEffect, useState } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider'; 
import { Loader2, X, Calendar, Clock, Send, FlaskConical } from 'lucide-react';

interface SampleSendModalProps {
  show: boolean;
  onClose: () => void;
  sampleId: string;
  onSampleSent: () => void;
}

const isMissingOrganizationSettingsJson = (error: unknown) =>
  String((error as { message?: string } | null)?.message ?? '').toLowerCase().includes('settings_json');

const SampleSendModal: React.FC<SampleSendModalProps> = ({ 
  show, 
  onClose, 
  sampleId,
  onSampleSent
}) => {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [labName, setLabName] = useState<string>('');
  const [labOptions, setLabOptions] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationSettings, setOrganizationSettings] = useState<Record<string, any>>({});
  
  // Initialize with current date and time
  const [sentDate, setSentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [sentTime, setSentTime] = useState<string>(new Date().toTimeString().slice(0, 5));
  
  useEffect(() => {
    if (show) {
      loadOrganizationLabs();
    }
  }, [show]);

  const loadOrganizationLabs = async () => {
    try {
      if (!user?.id) return;
      const { data: orgUser, error: orgUserError } = await supabase
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user?.id)
        .single();

      if (orgUserError) throw orgUserError;

      setOrganizationId(orgUser.organization_id);

      const { data: organization, error: organizationError } = await supabase
        .from('organizations')
        .select('settings_json')
        .eq('id', orgUser.organization_id)
        .single();

      if (organizationError && !isMissingOrganizationSettingsJson(organizationError)) throw organizationError;

      const settings = organization?.settings_json ?? {};
      const labs = Array.isArray(settings.labs) ? settings.labs.filter((lab: string) => !!lab) : [];
      const availableLabs = labs.length > 0 ? labs : ['Krone'];

      setOrganizationSettings(settings);
      setLabOptions(availableLabs);
      setLabName((prev) => prev || availableLabs[0] || '');
    } catch (err) {
      console.error('Error loading organization labs:', err);
    }
  };

  const updateOrganizationLabs = async (labValue: string) => {
    if (!organizationId) return;
    const normalized = labValue.trim();
    if (!normalized) return;

    const currentLabs = Array.isArray(organizationSettings.labs) ? organizationSettings.labs : [];
    const lower = normalized.toLowerCase();
    const nextLabs = [normalized, ...currentLabs.filter((lab: string) => lab.toLowerCase() !== lower)];
    const nextSettings = { ...organizationSettings, labs: nextLabs };

    const updateResult = await supabase
      .from('organizations')
      .update({ settings_json: nextSettings })
      .eq('id', organizationId);

    if (updateResult.error) {
      if (!isMissingOrganizationSettingsJson(updateResult.error)) {
        console.error('Error updating organization labs:', updateResult.error);
      }
      return;
    }

    setOrganizationSettings(nextSettings);
    setLabOptions(nextLabs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (!user) throw new Error('Benutzer nicht angemeldet');
      if (!labName.trim()) throw new Error('Bitte ein Labor auswählen');
      
      // Update the sample status to 'sent' (was 'send' before) and save the sent date/time
      const { error } = await supabase
        .from('customer_samples')
        .update({
          status: 'sent',
          sent_date: sentDate,
          sent_time: sentTime,
          sent_by: user.full_name || user.id || 'Unbekannt',
          sent_by_uuid: user.id,
          lab_name: labName.trim()
        })
        .eq('id', sampleId);
        
      if (error) throw error;

      await updateOrganizationLabs(labName);
      
      onSampleSent();
      onClose();
    } catch (err: any) {
      console.error('Error sending sample:', err);
      setError(err.message || 'Fehler beim Speichern des Versands');
    } finally {
      setLoading(false);
    }
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Send className="h-5 w-5 mr-2 text-blue-500" />
            Probe versenden
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FlaskConical className="h-4 w-4 mr-1 text-gray-400" />
              Labor
            </label>
            <input
              type="text"
              value={labName}
              onChange={(e) => setLabName(e.target.value)}
              placeholder="z. B. Krone"
              list="sample-send-lab-options"
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
            <datalist id="sample-send-lab-options">
              {labOptions.map((lab) => (
                <option key={lab} value={lab} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Vorschläge basieren auf den bisher verwendeten Laboren Ihrer Organisation.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
              Versanddatum
            </label>
            <input
              type="date"
              value={sentDate}
              onChange={(e) => setSentDate(e.target.value)}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="h-4 w-4 mr-1 text-gray-400" />
              Versandzeit
            </label>
            <input
              type="time"
              value={sentTime}
              onChange={(e) => setSentTime(e.target.value)}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Wird gespeichert...
                </>
              ) : (
                'Probe versenden'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SampleSendModal;
